import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productApi } from '../../services/privateFundApi';
import './ProductForm.css';

/**
 * 产品表单页面（新增/编辑）
 */
const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  
  const [formData, setFormData] = useState({
    name: '',
    purchaseTime: '',
    purchaseAmount: '',
    purchaseNetValue: '',
    purchaseShares: '',
    investors: '',
    historyNote: ''
  });
  const [calculatedShares, setCalculatedShares] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productApi.getProductById(id);
      if (response.success) {
        const product = response.data;
        setFormData({
          name: product.name || '',
          purchaseTime: product.purchaseTime ? new Date(product.purchaseTime).toISOString().split('T')[0] : '',
          purchaseAmount: product.purchaseAmount || '',
          purchaseNetValue: product.purchaseNetValue || '',
          purchaseShares: product.purchaseShares || '',
          investors: product.investors || '',
          historyNote: product.historyNote || ''
        });
      }
    } catch (err) {
      console.error('获取产品失败:', err);
      setError(err.message || '获取产品失败');
    } finally {
      setLoading(false);
    }
  };

  // 自动计算申购份额
  useEffect(() => {
    if (formData.purchaseAmount && formData.purchaseNetValue) {
      const amount = parseFloat(formData.purchaseAmount);
      const netValue = parseFloat(formData.purchaseNetValue);
      if (amount > 0 && netValue > 0) {
        const shares = amount / netValue;
        setCalculatedShares(shares.toFixed(2));
      } else {
        setCalculatedShares(null);
      }
    } else {
      setCalculatedShares(null);
    }
  }, [formData.purchaseAmount, formData.purchaseNetValue]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      const submitData = {
        name: formData.name.trim(),
        purchaseTime: formData.purchaseTime,
        purchaseAmount: parseFloat(formData.purchaseAmount),
        purchaseNetValue: parseFloat(formData.purchaseNetValue),
        investors: formData.investors.trim(),
        historyNote: formData.historyNote.trim()
      };

      if (formData.purchaseShares) {
        submitData.purchaseShares = parseFloat(formData.purchaseShares);
      }

      let response;
      if (isEdit) {
        response = await productApi.updateProduct(id, submitData);
      } else {
        response = await productApi.createProduct(submitData);
      }

      if (response.success) {
        navigate('/private-fund');
      } else {
        setError(response.message || '操作失败');
      }
    } catch (err) {
      console.error('保存产品失败:', err);
      setError(err.message || '保存产品失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/private-fund');
  };

  if (loading && isEdit) {
    return (
      <div className="product-form">
        <div className="loading-message">加载中...</div>
      </div>
    );
  }

  return (
    <div className="product-form">
      <div className="form-header">
        <button className="back-button" onClick={handleCancel}>
          ← 返回
        </button>
        <h1 className="form-title">{isEdit ? '编辑产品' : '添加产品'}</h1>
      </div>

      <div className="form-content">
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">产品名称 *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="请输入产品名称"
            />
          </div>

          <div className="form-group">
            <label htmlFor="purchaseTime">申购时间 *</label>
            <input
              type="date"
              id="purchaseTime"
              name="purchaseTime"
              value={formData.purchaseTime}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="purchaseAmount">申购金额（元） *</label>
              <input
                type="number"
                id="purchaseAmount"
                name="purchaseAmount"
                value={formData.purchaseAmount}
                onChange={handleChange}
                required
                min="0.01"
                step="0.01"
                placeholder="请输入申购金额"
              />
            </div>

            <div className="form-group">
              <label htmlFor="purchaseNetValue">申购净值 *</label>
              <input
                type="number"
                id="purchaseNetValue"
                name="purchaseNetValue"
                value={formData.purchaseNetValue}
                onChange={handleChange}
                required
                min="0.01"
                step="0.0001"
                placeholder="请输入申购净值"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="purchaseShares">申购份额</label>
            <input
              type="number"
              id="purchaseShares"
              name="purchaseShares"
              value={formData.purchaseShares}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="自动计算或手动输入"
            />
            {calculatedShares && (
              <p className="hint">自动计算: {calculatedShares}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="investors">投资人员</label>
            <textarea
              id="investors"
              name="investors"
              value={formData.investors}
              onChange={handleChange}
              rows="4"
              placeholder="格式：姓名 金额（万元）&#10;例如：&#10;邓博 30&#10;唐铖 20&#10;国英 20"
            />
            <p className="hint">每行一个投资人员，格式：姓名 金额（万元）</p>
          </div>

          <div className="form-group">
            <label htmlFor="historyNote">历史说明</label>
            <textarea
              id="historyNote"
              name="historyNote"
              value={formData.historyNote}
              onChange={handleChange}
              rows="4"
              placeholder="可选：记录历史净值与份额说明"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleCancel}>
              取消
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;























